import { payloadPublisher, TenantType } from '@atlassian/ufo';
import AnalyticsWebClient, {
  type ProductInfoType,
  type ScreenEventPayload,
  tenantType,
  type TrackEventPayload,
  type UIEventPayload,
  userType
} from '@atlassiansox/analytics-web-client';
import * as Sentry from '@sentry/react';
import { debounce } from 'lodash-es';

import { getCommitTimestamp } from '@canvas/buildtime-constants';
import { isSyntheticCheckMode } from '@canvas/testing/synthetic-check-mode';

import {
  type AddEntryEventData,
  type AddGroupButtonEventData,
  type AddGroupFieldPopupSelectedEventData,
  type AddGroupSelectedEventData,
  type AiFieldCreatedEventData,
  type AiFieldPreviewGeneratedEventData,
  type AiFieldRunEventData,
  type AiFieldRunFailedEventData,
  type AiFieldSettingsUpdatedEventData,
  type AiFieldSetupAbandonedEventData,
  type AiFieldSetupModalOpenedEventData,
  type AiFieldTypeSelectedEventData,
  type AiFieldValueManuallyEditedEventData,
  type AiSummaryChangeboardingShownEventData,
  type AiSummaryUnifiedEventData,
  type CellContentHasBeenModifiedEventData,
  type CustomCommentsEventPayload,
  type CustomScreenEventPayload,
  type CustomTrackEventPayload,
  type CustomUIEventPayload,
  type DatabaseCalculationTypeConvertedData,
  type DatabaseBEStreamingRevealEventData,
  DatabaseContainerType,
  type DatabaseCreateCalculationEventData,
  type DatabaseProfiledEventData,
  type DatabaseRemoveCalculationEventData,
  type DragToFillAppliedEventData,
  type DatasourcesDBMigrationEventData,
  type DatasourcesEventData,
  type EntryLinkFieldLoadData,
  type ColumnReorderedEventData,
  type EntryReorderedEventData,
  type ExportDatabaseEventData,
  type FirstEditEventData,
  type FirstEditTrackEventPayload,
  type FieldOptionEventData,
  type FrozenColumnsUpdatedEventData,
  type GenericEventData,
  type SelectionsAnalyticsAttributes,
  type GroupByFieldSelectedEventData,
  type GroupButtonClickedEventData,
  type SortButtonClickedEventData,
  type FilterButtonClickedEventData,
  type HideFieldsButtonClickedEventData,
  type GroupReorderedEventData,
  type GroupSortTypeChangedEventData,
  type HideAllColumnsEventData,
  type HideColumnsEventData,
  type ImportCancelledEventData,
  type ImportEntriesEventData,
  type JiraFieldUpdatedEventData,
  type JiraIssueEventData,
  type KeyboardNavigationEventData,
  type LayoutSelectedEventData,
  type LayoutSettingSelectedEventData,
  MacroType,
  type MentionNotificationFlagViewedEventData,
  type NumberedEntriesToggledEventData,
  type PageCreationFieldSettingEditedEventData,
  type PanelResizedEventData,
  type RemoveSubgroupEventData,
  type ReplaceDatabaseClickedEventData,
  type SendConnectionClickedEventData,
  type SendCreateDatabaseEntryEventData,
  type SendCreateDatabaseFieldEventData,
  type SendDeleteFieldEventData,
  type SendCreatePageFromDatabaseEventData,
  type SendAIResultActionedEventData,
  type SendRichTextFormattingAppliedEventData,
  type MediaFormatFieldSettingClickedEventData,
  type SendDatabaseContentViewedData,
  type SendDatabaseCreatedViewEventData,
  type SendDatabaseFieldSettingsOpenedEventData,
  type SendDatabaseFieldSettingsUpdatedEventData,
  type SendDatabaseDuplicateViewEventData,
  type SendDatabaseEditViewEventData,
  type SendDatabaseFilterData,
  type SendDatabaseSortEventData,
  type SendDeleteEntryEvent,
  type SendHeaderCheckboxSelectedEventData,
  type SendMacroCreatedEventData,
  type SendMentionNotificationCheckboxToggledEvent,
  type SendPageCreatedByFlowEventData,
  type SendRowCheckboxSelectedEventData,
  type SendSelectedActionItemEventData,
  type SetUpPanelTabClickedEventData,
  type SubgroupCollapseExpandEventData,
  type SystemTemplateEventData,
  type TemplateClickedEventData,
  type TimelineEntryDraggedEventData,
  type TimelineEntryResizedEventData,
  type UndoEventData,
  type ValueContextMenuOpenedEventData,
  type ViewEventData,
  type ViewUpdatedEventData,
  type SortRemovedEventData,
  type SortClearedEventData,
  type FilterRemovedEventData,
  type FilterClearedEventData
} from './types.ts';

const NATIVE_COLLAB_SERVICE = 'ncs';

export interface AnalyticsClientParams {
  cloudId: string | undefined;
  userId: string | undefined;
  spaceId: string | undefined;
  env: string;
  version: string;
  /**
   * Resolver injected at construction to break a circular dependency between
   * @databases/analytics and @databases/presence. The client calls this lazily
   * when enriching cell-content-modified events with the count of other
   * concurrent editors.
   */
  concurrentEditorCountResolver?: () => number;
}

export class AnalyticsClient {
  client: AnalyticsWebClient;
  spaceId: string | undefined;
  #concurrentEditorCountResolver: (() => number) | undefined;

  entryLinkFieldBatch: Set<string> = new Set();

  constructor({
    cloudId,
    userId,
    spaceId,
    env,
    version,
    concurrentEditorCountResolver
  }: AnalyticsClientParams) {
    this.#concurrentEditorCountResolver = concurrentEditorCountResolver;

    const productInfo: ProductInfoType = {
      env,
      product: 'confluence',
      version
    };

    this.client = new AnalyticsWebClient(productInfo);
    if (cloudId !== undefined) {
      this.client.setTenantInfo(tenantType.CLOUD_ID, cloudId);
    } else {
      this.client.setTenantInfo(tenantType.NONE);
    }
    this.client.setUserInfo(
      userType.ATLASSIAN_ACCOUNT,
      userId || this.client.getAnonymousId()
    );
    this.spaceId = spaceId;

    payloadPublisher.setup({
      gasv3: this.client,
      app: {
        version: { web: getCommitTimestamp()?.toString() ?? 'unknown' }
      },
      tenantType: isSyntheticCheckMode() ? TenantType.Synthetic : TenantType.RealUser,
      product: 'confluence-databases'
    });
  }

  async sendRichTextFormattingAppliedEvent(
    data: SendRichTextFormattingAppliedEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'applied',
      actionSubject: 'richTextFormatting',
      objectId: data.contentId,
      source: 'richTextFormattingToolbar',
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        formattingType: data.formattingType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendAIResultActionedEvent(data: SendAIResultActionedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'actioned',
      actionSubject: 'aiResult',
      actionSubjectId: 'client',
      source: 'AIMate',
      attributes: {
        aiFeatureName: 'aiChat',
        aiResultAction: 'accepted',
        experienceId: 'ai-mate',
        invokedFrom: 'Database AI',
        messageActions: [data.messageAction],
        // custom elements
        databaseContainer: data.databaseContainer,
        totalElements: data.totalElements,
        remainingElements: data.remainingElements,
        // required for unified event instrumentation
        singleInstrumentationID: data.singleInstrumentationID,
        isAIFeature: 1,
        userGeneratedAI: 1,
        proactiveGeneratedAI: 0,
        proactiveAIGenerated: 0,
        agentActions: [],
        agentIsDefault: true,
        agentIsVerified: false,
        isInSmartCreateFlow: true
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendAiSummaryInteractionInitiatedEvent(
    data: AiSummaryUnifiedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'initiated',
      actionSubject: 'aiInteraction',
      actionSubjectId: 'client',
      objectId: data.contentId,
      source: data.source,
      attributes: this.#getAiSummaryUnifiedAttributes(data)
    });
  }

  async sendAiSummaryResultViewedEvent(
    data: AiSummaryUnifiedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'viewed',
      actionSubject: 'aiResult',
      actionSubjectId: 'client',
      objectId: data.contentId,
      source: data.source,
      attributes: this.#getAiSummaryUnifiedAttributes(data)
    });
  }

  async sendDatabaseContentViewedEvent(
    data: SendDatabaseContentViewedData
  ): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'databaseContentScreen',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        locale: data.locale
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendDatabaseBEStreamingRevealEvent(
    data: DatabaseBEStreamingRevealEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: data.action,
      actionSubject: 'databaseEntryReveal',
      actionSubjectId: 'backendAgent',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        entryCount: data.entryCount,
        batchSize: data.batchSize,
        ...(data.action === 'started' ? {} : { durationMs: data.durationMs }),
        ...(data.action === 'cancelled'
          ? { cancellationReason: data.cancellationReason }
          : {})
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseImportedEvent(data: ImportEntriesEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'imported',
      actionSubject: 'databaseEntries',
      actionSubjectId: data.contentId,
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        isEmptyDatabase: data.isEmptyDatabase,
        fileSize: data.fileSize,
        importMethod: data.importMethod
      },
      source: 'importEntriesDialog'
    };

    return await this.#sendTrackEvent(event);
  }

  async sendContentPastedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'pasted',
      actionSubject: 'content',
      actionSubjectId: data.contentId,
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      },
      source: 'database'
    };

    return await this.#sendTrackEvent(event);
  }

  async sendFirstEditEvent(data: FirstEditEventData): Promise<boolean> {
    const event: FirstEditTrackEventPayload = {
      action: 'edited',
      actionSubject: 'content',
      actionSubjectId: data.contentId,
      objectType: 'database',
      objectId: data.contentId,
      attributes: {
        isAIGenerated: data.isAIGenerated
      },
      source: 'database'
    };

    return await this.#sendTrackEvent(event);
  }

  async sendUndoEvent(data: UndoEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'undo',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        action: data.action,
        type: data.type,
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDatabaseImportCancelledEvent(
    data: ImportCancelledEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'importCancelled',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDatabaseImportClickedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'importDatabase',
      objectId: data.contentId,
      source: 'setupPanel',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDatabaseExportedEvent(data: ExportDatabaseEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'exported', // unsure
      actionSubject: 'database',
      objectId: data.databaseId,
      actionSubjectId: data.databaseId,
      attributes: {
        applyViewOnExport: data.applyViewOnExport,
        databaseContainer: data.databaseContainer,
        exportFormat: data.exportFormat
      },
      source: 'exportDatabaseDialog'
    };
    return await this.#sendTrackEvent(event);
  }

  async sendColumnResizedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'resized',
      actionSubject: 'field',
      actionSubjectId: 'fieldBorder',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendInsertColumnEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'inserted',
      actionSubject: 'field',
      actionSubjectId: 'entryColumn',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDragToFillAppliedEvent(data: DragToFillAppliedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'applied',
      actionSubject: 'dragToFill',
      actionSubjectId: 'fillHandle',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendColumnReorderedEvent(data: ColumnReorderedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'reordered',
      actionSubject: 'field',
      actionSubjectId: 'fieldHeader',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendEntryReorderedEvent(data: EntryReorderedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'reordered',
      actionSubject: 'entry',
      actionSubjectId: data.entryType,
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendGroupReorderedEvent(data: GroupReorderedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'reordered',
      actionSubject: 'group',
      actionSubjectId: 'manualDragValueList',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        numberOfGroups: data.numberOfGroups,
        isSubgroup: data.isSubgroup
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendInsertRowEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'inserted',
      actionSubject: 'entry',
      actionSubjectId: 'entryRow',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendTimelineEntryDraggedEvent(
    data: TimelineEntryDraggedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'dragged',
      actionSubject: 'entry',
      actionSubjectId: 'timelineBar',
      objectId: data.contentId,
      source: 'timelineLayout',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendTimelineEntryResizedEvent(
    data: TimelineEntryResizedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'resized',
      actionSubject: 'entry',
      actionSubjectId: 'timelineBar',
      objectId: data.contentId,
      source: 'timelineLayout',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendHideColumnsEvent(data: HideColumnsEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'hid',
      actionSubject: 'field',
      actionSubjectId: 'fieldOption',
      source: data.source,
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        hidden: data.hidden,
        fieldType: data.fieldType
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendHideAllColumnsEvent(data: HideAllColumnsEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'hidAll',
      actionSubject: 'field',
      actionSubjectId: 'fieldOption',
      source: data.source,
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        hidden: data.hidden
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendFrozenColumnsUpdatedEvent(
    data: FrozenColumnsUpdatedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: data.action,
      actionSubject: 'field',
      actionSubjectId: 'frozenColumns',
      source: data.source,
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        previousFrozenColumnCount: data.previousFrozenColumnCount,
        nextFrozenColumnCount: data.nextFrozenColumnCount,
        targetFieldIndex: data.targetFieldIndex,
        maxFrozenColumnCount: data.maxFrozenColumnCount,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendLayoutSelectedEvent(data: LayoutSelectedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'databaseLayout',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        layoutType: data.layoutType,
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendLayoutSettingSelectedEvent(
    data: LayoutSettingSelectedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'databaseLayoutSetting',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        layoutSetting: data.layoutSetting,
        value: data.value,
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendNumberedEntriesToggledEvent(
    data: NumberedEntriesToggledEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'toggled',
      actionSubject: 'option',
      actionSubjectId: 'numberedEntriesToggle',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        value: data.value
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendConnectionInsertedEvent(data: DatasourcesEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'connectionField',
      action: 'inserted',
      objectId: data.contentId,
      source: 'datasourceDialog',
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        datasourceType: data.datasourceType,
        totalEntries: data.totalEntries
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendConnectionClickedEvent(
    data: SendConnectionClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      objectId: data.contentId,
      actionSubjectId: data.connectionType,
      source: 'setupPanel',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendDatabaseSortedEvent(data: SendDatabaseSortEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseSort',
      action: 'updated',
      source: data.source,
      actionSubjectId: data.fieldId,
      objectId: data.contentId,
      attributes: {
        isAscending: data.isAscending,
        isCustomOrder: data.isCustomOrder,
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseFilteredEvent(data: SendDatabaseFilterData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'database',
      action: 'filtered',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        fieldType: data.fieldType,
        filterType: data.filterType,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseCreateViewEvent(
    data: SendDatabaseCreatedViewEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseView', // TODO might need a rename
      action: 'created',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer,
        layoutType: data.layoutType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseDuplicateViewEvent(
    data: SendDatabaseDuplicateViewEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseView',
      action: 'duplicated',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer,
        layoutType: data.layoutType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseEditViewEvent(data: SendDatabaseEditViewEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      actionSubject: 'button',
      action: 'clicked',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: 'editDatabaseView',
      attributes: {
        viewId: data.viewId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendDatabaseFieldSettingsOpenedEvent(
    data: SendDatabaseFieldSettingsOpenedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      actionSubject: 'fieldSettings',
      action: 'opened',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendDatabaseFieldSettingsUpdatedEvent(
    data: SendDatabaseFieldSettingsUpdatedEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'fieldSettings',
      action: 'updated',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType,
        settingName: data.settingName
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseFieldOptionUpdatedEvent(
    data: FieldOptionEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'fieldOption',
      action: 'selected',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldSelection: data.fieldSelection,
        isNewField: data.isNewField
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendPageCreationFieldSettingEditedEvent(
    data: PageCreationFieldSettingEditedEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'edited',
      actionSubject: 'pageCreationFieldSetting',
      actionSubjectId: data.fieldId,
      objectId: data.contentId,
      source: 'fieldSettingsDialog',
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldSetting: data.fieldSetting,
        value: data.value
      }
    };
    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseJiraIssue(data: JiraIssueEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'jiraField',
      action: 'selected',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        jiraFieldSelection: data.jiraFieldSelection
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendJiraFieldUpdatedEvent(
    data: JiraFieldUpdatedEventData,
    actionSubject: string,
    source: string
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject,
      action: 'updated',
      objectId: data.contentId,
      source,
      actionSubjectId: data.issueKey,
      attributes: {
        databaseContainer: data.databaseContainer,
        issueKey: data.issueKey,
        fieldType: data.fieldType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatasourcesDBMigrationConvertIdFailedEvent(
    data: DatasourcesDBMigrationEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'datasourcesDBMigration',
      action: 'convertIdFailed',
      source: data.source,
      objectId: data.contentId,
      actionSubjectId: data.contentId,
      attributes: {
        missingField: data.missingField,
        cloudId: data.cloudId,
        serverId: data.serverId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseCellContentHasBeenModifiedEvent(
    data: CellContentHasBeenModifiedEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'cellContent',
      action: 'updated',
      objectId: data.contentId,
      source: 'databaseView',
      actionSubjectId: data.contentId,
      attributes: {
        activationId: data.activationId,
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType,
        concurrentEditorCount: this.#concurrentEditorCountResolver?.(),
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseSetupPanelClicked(
    data: SetUpPanelTabClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'tab',
      actionSubjectId: data.actionSubjectId,
      source: 'setupPanel',
      attributes: {
        databaseContainer: data.databaseContainer,
        isInitialDatabase: data.isInitialDatabase
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDatabaseViewSelected(data: ViewEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: 'databaseViewList',
      attributes: {
        viewId: data.viewId,
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  sendDatabaseViewReorderedEvent(data: ViewEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'reordered',
      actionSubject: 'databaseView',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return this.#sendTrackEvent(event);
  }

  async sendDatabasedSearchedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseSearch',
      action: 'searched',
      objectId: data.contentId,
      source: 'databaseSearch',
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendCreateDatabaseFieldEvent(
    data: SendCreateDatabaseFieldEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseField',
      action: 'created',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendCreateDatabaseEntryEvent(
    data: SendCreateDatabaseEntryEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseEntry',
      action: 'created',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseCommentCreatedEvent(data: {
    contentId: string;
    databaseContainer: DatabaseContainerType;
    commentId: string;
    parentCommentContentId?: string;
  }): Promise<boolean> {
    const event: CustomCommentsEventPayload = {
      action: 'created',
      actionSubject: 'comment',
      actionSubjectId: data.commentId,
      objectId: data.contentId,
      attributes: {
        commentType: 'inline',
        databaseContainer: data.databaseContainer,
        ...(data.parentCommentContentId
          ? { parentCommentId: data.parentCommentContentId }
          : undefined)
      },
      source: 'addCommentPopup'
    };
    return await this.#sendTrackEvent(event);
  }

  sendCreatePageFromDatabaseEvent(
    data: SendCreatePageFromDatabaseEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'page',
      action: 'created',
      objectId: data.newPageId,
      source: 'database',
      actionSubjectId: data.contentId,
      objectType: 'page',
      attributes: {
        databaseContainer: data.databaseContainer,
        sourceId: data.contentId,
        parentId: data.parentId,
        pageLocation: data.pageLocation,
        templateId: data.templateId,
        hasEntryEmbed: data.hasEntryEmbed,
        contentId: data.newPageId,
        pageSubType: data.pageSubType,
        version: 1,
        editorVersion: 'fabric'
      }
    };

    return this.#sendTrackEvent(event);
  }

  async sendPageCreatedByFlowEvent(
    data: SendPageCreatedByFlowEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'createdByFlow',
      actionSubject: 'content',
      objectId: data.contentId,
      source: 'databasePageLinkPopoverEdit',
      attributes: {
        flow: 'databasePageLinkPopoverEdit',
        contentType: 'page',
        databaseContainer: data.databaseContainer,
        isLivePage: data.isLivePage,
        ...(data.isLivePage && { collabService: NATIVE_COLLAB_SERVICE })
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendAddEntryButtonClickedEvent(data: AddEntryEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addEntry',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendMediaFormatFieldSettingButtonClickedEvent(
    data: MediaFormatFieldSettingClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'mediaFormatFieldSetting',
      objectId: data.contentId,
      source: 'fieldSettingsDialog',
      attributes: {
        databaseContainer: data.databaseContainer,
        mediaFormat: data.mediaFormat
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendDeleteFieldEvent(data: SendDeleteFieldEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseField',
      action: 'deleted',
      objectId: data.contentId,
      source: 'fieldSettingsDialog',
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldTypes: 'fieldTypes' in data ? data.fieldTypes : [data.fieldType],
        ...('fieldIdDeleted' in data
          ? {
              fieldIdDeleted: data.fieldIdDeleted,
              fieldName: data.fieldName
            }
          : {})
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendAiFieldTypeSelectedEvent(
    data: AiFieldTypeSelectedEventData
  ): Promise<boolean> {
    return await this.#sendUIEvent({
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'aiFieldType',
      objectId: data.contentId,
      source: 'addFieldContextualMenu',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldPickerSessionId: data.fieldPickerSessionId,
        aiFieldType: data.aiFieldType
      }
    });
  }

  async sendAiFieldSetupModalOpenedEvent(
    data: AiFieldSetupModalOpenedEventData
  ): Promise<boolean> {
    return await this.#sendUIEvent({
      action: 'opened',
      actionSubject: 'modal',
      actionSubjectId: 'aiFieldSetupModal',
      objectId: data.contentId,
      source: 'aiFieldSetupModal',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        modalSessionId: data.modalSessionId,
        aiFieldType: data.aiFieldType,
        openedFrom: data.openedFrom
      }
    });
  }

  async sendAiFieldPreviewGeneratedEvent(
    data: AiFieldPreviewGeneratedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'generated',
      actionSubject: 'aiFieldPreview',
      actionSubjectId: 'aiFieldPreview',
      objectId: data.contentId,
      source: 'addFieldContextualMenu',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        aiFieldType: data.aiFieldType,
        config: { outputFormat: data.outputFormat }
      }
    });
  }

  async sendAiFieldCreatedEvent(data: AiFieldCreatedEventData): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'created',
      actionSubject: 'aiField',
      actionSubjectId: 'aiField',
      objectId: data.contentId,
      source: 'aiFieldSetupModal',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        aiFieldType: data.aiFieldType
      }
    });
  }

  async sendAiFieldSetupAbandonedEvent(
    data: AiFieldSetupAbandonedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'abandoned',
      actionSubject: 'aiFieldSetup',
      actionSubjectId: 'aiFieldSetup',
      objectId: data.contentId,
      source: 'aiFieldSetupModal',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        aiFieldType: data.aiFieldType,
        exitAction: data.exitAction,
        previewGenerated: data.previewGenerated,
        config: { outputFormat: data.outputFormat }
      }
    });
  }

  async sendAiFieldRunStartedEvent(data: AiFieldRunEventData): Promise<boolean> {
    return await this.#sendAiFieldRunEvent('started', data);
  }

  async sendAiFieldRunCompletedEvent(data: AiFieldRunEventData): Promise<boolean> {
    return await this.#sendAiFieldRunEvent('completed', data);
  }

  async sendAiFieldRunFailedEvent(data: AiFieldRunFailedEventData): Promise<boolean> {
    return await this.#sendAiFieldRunEvent('failed', data, {
      errorCategory: data.errorCategory,
      errorCode: data.errorCode
    });
  }

  async sendAiFieldSettingsUpdatedEvent(
    data: AiFieldSettingsUpdatedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'updated',
      actionSubject: 'aiFieldSettings',
      actionSubjectId: 'aiFieldSettings',
      objectId: data.contentId,
      source: 'fieldSettingsDialog',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        aiFieldType: data.aiFieldType,
        format: data.format,
        previousFormat: data.previousFormat
      }
    });
  }

  async sendAiFieldValueManuallyEditedEvent(
    data: AiFieldValueManuallyEditedEventData
  ): Promise<boolean> {
    return await this.#sendTrackEvent({
      action: 'edited',
      actionSubject: 'aiFieldValue',
      actionSubjectId: 'aiFieldValue',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        entryId: data.entryId,
        aiFieldType: data.aiFieldType,
        overrideTarget: data.overrideTarget
      }
    });
  }

  async sendAiSummaryChangeboardingShownEvent(
    data: AiSummaryChangeboardingShownEventData
  ): Promise<boolean> {
    return await this.#sendUIEvent({
      action: 'shown',
      actionSubject: 'aiSummaryChangeboardingModal',
      actionSubjectId: 'aiFields',
      objectId: data.contentId,
      source: 'fieldPicker',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        step: 'aiSectionPulse',
        treatment: 'pulse'
      }
    });
  }

  async sendDeleteEntryEvent(data: SendDeleteEntryEvent): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseEntry',
      action: 'deleted',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendMacroCreatedEvent(data: SendMacroCreatedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseMacro',
      action: 'created',
      objectId: data.contentId,
      source: data.source ?? this.#getSourceFromCommand(data.macroType),
      actionSubjectId: data.contentId,
      attributes: {
        macroType: data.macroType,
        databaseContainer: DatabaseContainerType.NONE,
        isNew: data.isNew
      }
    };

    return await this.#sendTrackEvent(event, false);
  }

  async sendEntryLinkClickedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      objectId: data.contentId,
      source: 'database',
      actionSubjectId: 'entryLink',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendReplaceDatabaseClickedEvent(
    data: ReplaceDatabaseClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      objectId: data.contentId,
      actionSubjectId: 'replaceDatabase',
      source: 'datasourceDialog',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendTemplateClickedEvent(data: TemplateClickedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      objectId: data.contentId,
      actionSubjectId: 'databaseTemplates',
      source: 'setupPanel',
      attributes: {
        databaseContainer: data.databaseContainer,
        isInitialDatabase: data.isInitialDatabase,
        templateType: data.templateType
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendSystemTemplateApplied(data: SystemTemplateEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'systemTemplateApplied',
      actionSubject: 'created',
      objectId: data.contentId,
      actionSubjectId: 'databaseTemplate',
      source: 'app',
      attributes: {
        databaseContainer: data.databaseContainer,
        templateType: data.templateType,
        templateLayout: data.templateLayout
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendViewUpdatedEvent(data: ViewUpdatedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseView',
      action: 'updated',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer,
        sortUpdated: data.sortUpdated,
        filterUpdated: data.filterUpdated,
        groupUpdated: data.groupUpdated,
        hideFieldsUpdated: data.hideFieldsUpdated,
        layoutTypeUpdated: data.layoutTypeUpdated,
        layoutSettingsUpdated: data.layoutSettingsUpdated,
        selectedLayout: data.selectedLayout,
        renamed: data.renamed,
        contextualMenuExperimentCohort: data.contextualMenuExperimentCohort
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendViewLinkCopiedEvent(data: ViewEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseView',
      action: 'copied',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDefaultViewSetEvent(data: ViewEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'defaultDatabaseView',
      action: 'set',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendViewDeletedEvent(data: ViewEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseView',
      action: 'deleted',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendViewSettingsPanelClosedEvent(data: ViewEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseViewSettingsPanel',
      action: 'closed',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.viewId,
      attributes: {
        databaseContainer: data.databaseContainer,
        isEdited: data.isEdited
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendSortRemovedEvent(data: SortRemovedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseSort',
      action: 'removed',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendSortClearedEvent(data: SortClearedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseSort',
      action: 'cleared',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendFilterRemovedEvent(data: FilterRemovedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseFilter',
      action: 'removed',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        isFilterGroup: data.isFilterGroup
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendFilterClearedEvent(data: FilterClearedEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseFilter',
      action: 'cleared',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendEmbedDatabaseTitleUpdatedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'updated',
      actionSubject: 'embedDatabaseHeaderTitle',
      actionSubjectId: data.contentId,
      objectId: data.contentId,
      source: 'database',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendAddTagFieldEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addTagField',
      objectId: data.contentId,
      source: 'boardLayout',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendAddDateFieldEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addDateField',
      objectId: data.contentId,
      source: 'calendarLayout',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendEntryLinkFieldAnalytics(data: EntryLinkFieldLoadData): Promise<void> {
    this.entryLinkFieldBatch.add(data.fieldId);
    await this.#debouncedSendBatchedEntryLinkFieldAnalytics(data);
  }

  async sendSetupPanelViewedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'databaseSetupPanel',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendUnsavedViewLoadedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      action: 'loaded',
      actionSubject: 'databaseUnsavedView',
      objectId: data.contentId,
      source: 'databaseViewLoader',
      actionSubjectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendTableEmptyStateScreenEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'tableEmptyStateScreen',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendBoardEmptyStateScreenEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'boardEmptyStateScreen',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendBoardLayoutScreenEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'boardLayoutScreen',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendFieldContextualMenuOpened(
    data: GenericEventData & SelectionsAnalyticsAttributes
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'dragHandle',
      actionSubjectId: 'fieldContextualMenu',
      source: 'tableLayout',
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendCreateCalculationEvent(
    data: DatabaseCreateCalculationEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'quickCalculation',
      action: 'created',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.fieldId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType,
        calculationType: data.calculationType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendRemoveCalculationEvent(
    data: DatabaseRemoveCalculationEventData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'quickCalculation',
      action: 'removed',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.fieldId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendCalculationEventConverted(
    data: DatabaseCalculationTypeConvertedData
  ): Promise<boolean> {
    const event: CustomTrackEventPayload = {
      actionSubject: 'quickCalculation',
      action: 'converted',
      objectId: data.contentId,
      source: data.source,
      actionSubjectId: data.fieldId,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType,
        fromCalculationType: data.fromCalculationType,
        toCalculationType: data.toCalculationType
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseCreatedEvent(data: GenericEventData) {
    const event: CustomTrackEventPayload = {
      actionSubject: 'database',
      action: 'created',
      source: 'database',
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendDatabaseProfiledEvent(data: DatabaseProfiledEventData) {
    const event: CustomTrackEventPayload = {
      actionSubject: 'databaseMetadata',
      action: 'profiled',
      source: 'database',
      objectId: data.contentId,
      attributes: {
        ...data.attributes,
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendTrackEvent(event);
  }

  async sendMentionNotificationCheckboxToggledEvent(
    data: SendMentionNotificationCheckboxToggledEvent
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'toggled',
      actionSubject: 'option',
      actionSubjectId: 'mentionNotificationToggle',
      objectId: data.contentId,
      source: 'databaseViewSettingsPanel',
      attributes: {
        databaseContainer: data.databaseContainer,
        checked: data.checked
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendMentionNotificationFlagViewedEvent(
    data: MentionNotificationFlagViewedEventData
  ): Promise<boolean> {
    const event: CustomScreenEventPayload = {
      name: 'mentionNotificationFlag',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        type: data.type
      }
    };

    return await this.#sendScreenEvent(event);
  }

  async sendMentionNotificationActionEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'mentionNotificationAction',
      objectId: data.contentId,
      source: 'flags',
      attributes: {
        action: 'fieldSettingsPanelOpened',
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendUserMentionTypeaheadClickedEvent(data: GenericEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'userMentionTypeahead',
      objectId: data.contentId,
      source: 'databaseView',
      attributes: {
        databaseContainer: data.databaseContainer
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendRowCheckboxSelectedEvent(
    data: SendRowCheckboxSelectedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'checkbox',
      actionSubjectId: 'selectRowCheckbox',
      source: 'databaseEntryCheckbox',
      objectId: data.contentId,
      attributes: {
        entryId: data.entryId,
        databaseContainer: data.databaseContainer,
        checkboxState: data.checkboxState,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendHeaderCheckboxSelectedEvent(
    data: SendHeaderCheckboxSelectedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'checkbox',
      actionSubjectId: 'selectAllCheckbox',
      source: 'databaseHeaderCheckbox',
      objectId: data.contentId,
      attributes: {
        entriesCount: data.entriesCount,
        databaseContainer: data.databaseContainer,
        checkboxState: data.checkboxState,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendKeyboardNavigationEvent(data: KeyboardNavigationEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: data.kind === 'availability' ? 'viewed' : 'triggered',
      actionSubject: 'keyboardNavigation',
      actionSubjectId: 'table',
      source: 'databaseTable',
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data),
        ...(data.kind === 'usage' && { actionFamily: data.actionFamily })
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendSelectedActionItemEvent(
    data: SendSelectedActionItemEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'actionItem',
      actionSubjectId: 'databaseActionBarItem',
      source: `${data.selectionMode}ActionBar`,
      attributes: {
        databaseContainer: data.databaseContainer,
        selectedCount: data.selectedCount,
        selectionMode: data.selectionMode,
        actionType: data.type,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendAddGroupButtonEvent(data: AddGroupButtonEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addGroupButton',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        isSubgroup: data.isSubgroup,
        numberOfGroups: data.numberOfGroups
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendRemoveSubgroupEvent(data: RemoveSubgroupEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'removeSubgroup',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        fieldType: data.fieldType
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendGroupByFieldSelectedEvent(
    data: GroupByFieldSelectedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'groupByField',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        selectedFieldType: data.selectedFieldType,
        isSubgroup: data.isSubgroup
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendGroupSortTypeChangedEvent(
    data: GroupSortTypeChangedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'groupSortType',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        selectedType: data.selectedType,
        numberOfGroups: data.numberOfGroups,
        isSubgroup: data.isSubgroup
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendSubgroupCollapsibleHeaderClickedEvent(
    data: SubgroupCollapseExpandEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'subgroupCollapsibleHeader',
      objectId: data.contentId,
      source: 'boardLayout',
      attributes: {
        databaseContainer: data.databaseContainer,
        state: data.state
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendGroupButtonClickedEvent(data: GroupButtonClickedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'group',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        group: data.group,
        subgroup: data.subgroup
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendSortButtonClickedEvent(data: SortButtonClickedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'sort',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        sortCount: data.sortCount
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendFilterButtonClickedEvent(
    data: FilterButtonClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'filter',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        filterCount: data.filterCount
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendHideFieldsButtonClickedEvent(
    data: HideFieldsButtonClickedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'hideFields',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        hiddenFieldCount: data.hiddenFieldCount
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendAddGroupSelectedEvent(data: AddGroupSelectedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'addGroup',
      objectId: data.contentId,
      source: data.source,
      attributes: {
        databaseContainer: data.databaseContainer,
        isSubgroup: data.isSubgroup,
        selectedFieldType: data.selectedFieldType
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendAddGroupFieldPopupSelectedEvent(
    data: AddGroupFieldPopupSelectedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'selected',
      actionSubject: 'option',
      actionSubjectId: 'addGroupFieldPopup',
      objectId: data.contentId,
      source: 'boardEmptyStateScreen',
      attributes: {
        databaseContainer: data.databaseContainer,
        selectedFieldType: data.selectedFieldType
      }
    };

    return await this.#sendUIEvent(event);
  }

  async sendPanelResizedEvent(data: PanelResizedEventData): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'resized',
      actionSubject: 'sidePanel',
      actionSubjectId: data.panelKey,
      objectId: data.contentId,
      source: 'database',
      attributes: {
        databaseContainer: data.databaseContainer,
        panelSize: data.panelSize
      }
    };
    return await this.#sendUIEvent(event);
  }

  async sendValueContextMenuOpenedEvent(
    data: ValueContextMenuOpenedEventData
  ): Promise<boolean> {
    const event: CustomUIEventPayload = {
      action: 'opened',
      actionSubject: 'menu',
      actionSubjectId: 'valueContextMenu',
      source: 'databaseView',
      objectId: data.contentId,
      attributes: {
        databaseContainer: data.databaseContainer,
        ...this.#extractSelectionsAnalyticsAttributes(data)
      }
    };
    return await this.#sendUIEvent(event);
  }

  sendOperationalEvent(): void {
    // this is required for AnalyticsClientInterface
  }

  /**
   * Extracts optional selections analytics attributes from event data.
   */
  #extractSelectionsAnalyticsAttributes(
    data: SelectionsAnalyticsAttributes
  ): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};
    if (data.databaseEntryCount !== undefined) {
      attrs.databaseEntryCount = data.databaseEntryCount;
    }
    if (data.databaseFieldCount !== undefined) {
      attrs.databaseFieldCount = data.databaseFieldCount;
    }
    return attrs;
  }

  #sendAiFieldRunEvent = async (
    action: 'started' | 'completed' | 'failed',
    data: AiFieldRunEventData,
    errorAttributes: { errorCategory: string; errorCode?: string } | undefined = undefined
  ): Promise<boolean> =>
    await this.#sendTrackEvent({
      action,
      actionSubject: 'aiFieldRun',
      actionSubjectId: 'aiFieldRun',
      objectId: data.contentId,
      source: action === 'started' ? data.source : 'aiFieldGeneration',
      attributes: {
        contentId: data.contentId,
        databaseContainer: data.databaseContainer,
        fieldId: data.fieldId,
        aiFieldType: data.aiFieldType,
        runId: data.runId,
        triggerType: data.triggerType,
        operation: data.operation,
        runMode: 'manual',
        runScope: data.runScope,
        ...(action === 'completed' ? { outcome: 'success' } : {}),
        ...errorAttributes
      }
    });

  #getAiSummaryUnifiedAttributes(data: AiSummaryUnifiedEventData) {
    return {
      aiFeatureName: 'aiSummary',
      aiFeatureId: 'ai-summary',
      singleInstrumentationID: data.singleInstrumentationID,
      traceId: data.traceId,
      isAIFeature: 1,
      userGeneratedAI: 1,
      proactiveGeneratedAI: 0,
      usecaseID: 'confluence-databases-ai-teammate',
      experienceID: 'confluence-databases-ai-teammate',
      databaseContainer: data.databaseContainer,
      ...(data.aiDwellTimeMilliSeconds === undefined
        ? {}
        : { aiDwellTimeMilliSeconds: data.aiDwellTimeMilliSeconds })
    };
  }

  #sendTrackEvent = async (
    event: TrackEventPayload,
    sendContainerType = true
  ): Promise<boolean> => {
    const newEventPayload = {
      containerId: this.spaceId,
      containerType: sendContainerType ? 'space' : undefined,
      objectType: 'database',
      ...event
    };

    await this.client.sendTrackEvent(newEventPayload);

    const baseBreadcrumbMessage = `${event.actionSubject} ${event.action}`;
    const objectIdSubMessage = event.objectId
      ? ` (database contentId: ${event.objectId})`
      : '';

    Sentry.addBreadcrumb({
      category: 'trackEvent',
      message: `${baseBreadcrumbMessage}${objectIdSubMessage}`,
      level: 'info'
    });

    return true;
  };

  #debouncedSendBatchedEntryLinkFieldAnalytics = debounce(
    async (data: EntryLinkFieldLoadData): Promise<boolean> => {
      if (this.entryLinkFieldBatch.size === 0) {
        return true;
      }

      const event: CustomTrackEventPayload = {
        actionSubject: 'failedEntryLinkReference',
        action: 'load',
        objectId: data.contentId,
        source: data.source,
        actionSubjectId: data.fieldId,
        attributes: {
          databaseContainer: data.databaseContainer,
          fields: Array.from(this.entryLinkFieldBatch),
          count: this.entryLinkFieldBatch.size
        }
      };

      this.entryLinkFieldBatch = new Set();

      return await this.#sendTrackEvent(event);
    },
    1000
  );

  #sendUIEvent = async (event: UIEventPayload): Promise<boolean> => {
    const newEventPayload = {
      containerId: this.spaceId,
      containerType: 'space',
      objectType: 'database',
      ...event
    };

    await this.client.sendUIEvent(newEventPayload);

    const baseBreadcrumbMessage = `${event.actionSubject} ${event.action} ${event.actionSubjectId}`;
    const objectIdSubMessage = event.objectId
      ? ` (database contentId: ${event.objectId})`
      : '';

    Sentry.addBreadcrumb({
      category: 'uiEvent',
      message: `${baseBreadcrumbMessage}${objectIdSubMessage}`,
      level: 'info'
    });

    return true;
  };

  #sendScreenEvent = async (event: ScreenEventPayload): Promise<boolean> => {
    const newEventPayload = {
      ...event,
      attributes: {
        objectType: 'database',
        containerId: this.spaceId,
        containerType: 'space',
        ...event.attributes
      }
    };

    await this.client.sendScreenEvent(newEventPayload);

    const baseBreadcrumbMessage = `viewed ${event.name}`;
    const objectIdSubMessage = event?.attributes?.contentId
      ? ` (database contentId: ${event.attributes.contentId})`
      : '';

    Sentry.addBreadcrumb({
      category: 'screenEvent',
      message: `${baseBreadcrumbMessage}${objectIdSubMessage}`,
      level: 'info'
    });

    return true;
  };

  #getSourceFromCommand(command: MacroType): string {
    switch (command) {
      case MacroType.DATABASE:
        return `createDatabaseDialog`;
      case MacroType.ENTRY:
        return `createDatabaseEntryDialog`;
      case MacroType.VALUE:
        return `createDatabaseValueDialog`;
    }
  }
}
